import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';

import { useAuth } from '../auth';
import { CompactTable } from '../components/CompactTable';
import {
  createPortalDisplayNumber,
  getPortalDisplayNumbers,
  PortalApiError,
  updatePortalDisplayNumber,
} from '../services/portalApi';
import type { PortalDisplayNumber, PortalDisplayNumberInput } from '../types/api';

const emptyForm: PortalDisplayNumberInput = {
  name: '',
  phoneNumber: '',
  isActive: true,
};

export function DisplayNumbersPage() {
  const { getAccessToken } = useAuth();
  const [items, setItems] = useState<PortalDisplayNumber[]>([]);
  const [form, setForm] = useState<PortalDisplayNumberInput>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(true);

  const loadItems = useCallback(async (): Promise<void> => {
    const token = await getAccessToken();
    const response = await getPortalDisplayNumbers(token);
    setItems(response.displayNumbers);
  }, [getAccessToken]);

  useEffect(() => {
    let cancelled = false;
    async function loadInitialItems(): Promise<void> {
      try {
        const token = await getAccessToken();
        const response = await getPortalDisplayNumbers(token);
        if (!cancelled) setItems(response.displayNumbers);
      } catch (error) {
        if (!cancelled) setMessage(toMessage(error));
      } finally {
        if (!cancelled) setIsBusy(false);
      }
    }
    void loadInitialItems();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      if (editingId) {
        await updatePortalDisplayNumber(token, editingId, form);
      } else {
        await createPortalDisplayNumber(token, form);
      }
      setEditingId(null);
      setForm(emptyForm);
      await loadItems();
    } catch (error) {
      setMessage(toMessage(error));
    } finally {
      setIsBusy(false);
    }
  }

  function beginEdit(item: PortalDisplayNumber): void {
    setEditingId(item.DisplayNumberId);
    setForm({
      name: item.Name,
      phoneNumber: item.PhoneNumber,
      isActive: item.IsActive,
    });
    setMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <section className="portal-section display-numbers-page">
      <Link className="back-link" to="/admin">← Back to administration</Link>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Administration</p>
          <h1>Caller display numbers</h1>
          <p className="hero-description">
            Give each outgoing number a clear name that Event Managers can select.
          </p>
        </div>
      </div>

      <form className="event-form" onSubmit={(event) => void handleSubmit(event)}>
        <h2>{editingId ? 'Edit display number' : 'New display number'}</h2>
        <label>
          Name
          <input required maxLength={100} value={form.name}
            placeholder="The Send"
            onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </label>
        <label>
          Phone number
          <input required value={form.phoneNumber}
            placeholder="+4712345678"
            onChange={(event) => setForm({ ...form, phoneNumber: event.target.value })} />
        </label>
        {editingId ? (
          <label className="checkbox-label">
            <input type="checkbox" checked={form.isActive}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
            Available for new and edited events
          </label>
        ) : null}
        <div className="event-form-actions">
          <button className="primary-button" disabled={isBusy} type="submit">
            {editingId ? 'Save changes' : 'Add display number'}
          </button>
          {editingId ? (
            <button className="secondary-button" type="button"
              onClick={() => { setEditingId(null); setForm(emptyForm); setMessage(null); }}>
              Cancel
            </button>
          ) : null}
        </div>
      </form>

      {message ? <p className="api-message" role="alert">{message}</p> : null}

      <section className="telephone-list-section">
        <h2>Display numbers</h2>
        {isBusy && items.length === 0 ? <p>Loading display numbers...</p> : null}
        {!isBusy && items.length === 0 ? (
          <div className="event-empty-state"><p>No display numbers have been added.</p></div>
        ) : null}
        {items.length > 0 ? (
          <CompactTable label="Caller display numbers" headers={[
            { key: 'name', label: 'Name' },
            { key: 'number', label: 'Phone number' },
            { key: 'status', label: 'Status' },
            { key: 'actions', label: '' },
          ]}>
            {items.map((item) => (
              <tr key={item.DisplayNumberId}>
                <td>{item.Name}</td>
                <td className="compact-table-mono">{item.PhoneNumber}</td>
                <td>
                  <span className={item.IsActive
                    ? 'user-status user-status-active'
                    : 'user-status user-status-blocked'}>
                    {item.IsActive ? 'Available' : 'Inactive'}
                  </span>
                </td>
                <td className="compact-table-actions">
                  <button className="table-action-button" type="button"
                    onClick={() => beginEdit(item)}>Edit</button>
                </td>
              </tr>
            ))}
          </CompactTable>
        ) : null}
      </section>
    </section>
  );
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError || error instanceof Error) return error.message;
  return 'The display number request could not be completed.';
}
